import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';

// Use full URL to avoid proxy issues. REACT_APP_API_URL = backend origin (with or without https://).
// Examples: https://leadpilot-production-ebfa.up.railway.app  or  leadpilot-production-ebfa.up.railway.app
const raw = (process.env.REACT_APP_API_URL || 'http://localhost:8000').trim();
// Auto-add https:// if missing (otherwise browser treats it as relative path -> wrong host)
const withScheme = raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;
const base = withScheme.replace(/\/$/, '');
const API_URL = base.endsWith('/api/v1') ? base : base + '/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Ensure we use the full URL, not relative paths
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  config.baseURL = API_URL;
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add token to requests - support both Clerk and JWT
api.interceptors.request.use(async (config) => {
  // Check for Clerk token in a way that works
  // Since we can't use hooks in interceptors, we'll store Clerk token in a way we can access
  const clerkToken = (window as any).__clerk_token;
  if (clerkToken) {
    config.headers.Authorization = `Bearer ${clerkToken}`;
    return config;
  }
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('=== API Response Error ===');
    console.error('URL:', error.config?.url);
    console.error('Status:', error.response?.status);
    console.error('Error Message:', error.message);
    console.error('Response Data:', error.response?.data);
    console.error('Full Error:', error);
    console.error('========================');
    
    // Handle 403 - Email not verified
    const detailStr = typeof error.response?.data?.detail === 'string'
      ? error.response.data.detail
      : getApiErrorMessage(error, '');
    if (error.response?.status === 403 && detailStr?.includes('Email not verified')) {
      console.warn('Email not verified - redirecting to verification page');
      window.location.href = '/verify-email-required';
      return Promise.reject(error);
    }
    // Handle 403 - Account locked (don't redirect, let the page show the error)
    if (error.response?.status === 403 && detailStr?.includes('Account locked')) {
      console.warn('Account locked:', detailStr);
      return Promise.reject(error);
    }
    
    // Handle 401 - Unauthorized
    if (error.response?.status === 401) {
      // Don't redirect on auth flows/pages
      if (
        !error.config?.url?.includes('/auth/login') &&
        !error.config?.url?.includes('/auth/register') &&
        !error.config?.url?.includes('/auth/invite') &&
        !error.config?.url?.includes('/auth/invite/accept') &&
        !error.config?.url?.includes('/auth/verify-email') &&
        !error.config?.url?.includes('/auth/send-verification-email') &&
        !error.config?.url?.includes('/auth/request-password-reset') &&
        !error.config?.url?.includes('/auth/reset-password')
      ) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Bulk add leads
export const bulkAddLeads = async (businessIds: number[]) => {
  const response = await api.post('/leads/bulk', {
    business_ids: businessIds
  });
  return response.data;
};

// Check which businesses are already in pipeline
export const checkLeadsInPipeline = async (businessIds: number[]): Promise<Record<string, boolean>> => {
  const idsString = businessIds.join(',');
  const response = await api.get(`/leads/check?business_ids=${idsString}`);
  return response.data;
};

// Location autocomplete (city, zip, country)
export const getLocationAutocomplete = async (
  input: string,
  country?: string | null
): Promise<{ predictions: Array<{ description: string; place_id?: string }> }> => {
  const params: Record<string, string> = { input: input.trim() };
  if (country && country.trim()) {
    params.country = country.trim();
  }
  const res = await api.get('/places/location-autocomplete', { params });
  return res.data;
};

export default api;
